import { socketService } from "./socketService";
import { reservationsApi } from "./reservationsApi";
import { inboundEmailsApi } from "./inboundEmailsApi";

/**
 * Redux middleware for Socket.IO real-time updates
 * Listens to socket events and dispatches Redux actions
 */
export const socketMiddleware = (storeAPI) => {
  let socketInitialized = false;
  let eventListenersRegistered = false;
  const eventHandlers = new Map(); // Store event handlers for cleanup

  /**
   * Initialize socket connection
   */
  const initializeSocket = async () => {
    if (socketInitialized) return;

    try {
      // Get base URL from environment or window.baseDomain
      const baseUrl =
        process.env.REACT_APP_BASE_DOMAIN ||
        window.baseDomain ||
        "https://api.happychef.cloud/";

      console.log("🔌 [socketMiddleware] Initializing socket connection...");

      const connected = await socketService.connect(baseUrl);

      if (connected) {
        console.log("✅ [socketMiddleware] Socket connected successfully");
        socketInitialized = true;
        registerEventListeners(storeAPI);
      } else {
        console.warn(
          "⚠️ [socketMiddleware] Socket connection failed, will use HTTP fallback"
        );
      }
    } catch (error) {
      console.error("❌ [socketMiddleware] Failed to initialize socket:", error);
    }
  };

  /**
   * Register socket event listeners for real-time updates
   */
  const registerEventListeners = (storeAPI) => {
    if (eventListenersRegistered) return;

    console.log("👂 [socketMiddleware] Registering event listeners...");

    // Helper to register and track event listeners
    const registerHandler = (event, handler) => {
      socketService.on(event, handler);
      if (!eventHandlers.has(event)) {
        eventHandlers.set(event, []);
      }
      eventHandlers.get(event).push(handler);
    };

    // Helper to dispatch reservation change events
    const notifyReservationChanged = (type, data) => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("redux:reservations/changed", { detail: { type, data } })
        );
      }
    };

    // Reservation created
    registerHandler("reservation:created", (data) => {
      console.log("📨 [socketMiddleware] Reservation created:", data);
      storeAPI.dispatch(reservationsApi.util.invalidateTags(["Reservations"]));
      notifyReservationChanged("created", data);
    });

    // Reservation updated
    registerHandler("reservation:updated", (data) => {
      console.log("📨 [socketMiddleware] Reservation updated:", data);
      storeAPI.dispatch(
        reservationsApi.util.invalidateTags([
          { type: "Reservations", id: data.id || data._id },
          "Reservations",
        ])
      );
      notifyReservationChanged("updated", data);
    });

    // Reservation deleted
    registerHandler("reservation:deleted", (data) => {
      console.log("📨 [socketMiddleware] Reservation deleted:", data);
      storeAPI.dispatch(reservationsApi.util.invalidateTags(["Reservations"]));
      notifyReservationChanged("deleted", data);
    });

    // Reservation status changed
    registerHandler("reservation:statusChanged", (data) => {
      console.log("📨 [socketMiddleware] Reservation status changed:", data);
      storeAPI.dispatch(
        reservationsApi.util.invalidateTags([
          { type: "Reservations", id: data.id || data._id },
          "Reservations",
        ])
      );
      notifyReservationChanged("statusChanged", data);
    });

    // Hold placed
    registerHandler("reservation:holdPlaced", (data) => {
      console.log("📨 [socketMiddleware] Hold placed:", data);
      storeAPI.dispatch(
        reservationsApi.util.invalidateTags([
          { type: "Reservations", id: data.id || data._id },
          "Reservations",
        ])
      );
      notifyReservationChanged("holdPlaced", data);
    });

    // Hold captured
    registerHandler("reservation:holdCaptured", (data) => {
      console.log("📨 [socketMiddleware] Hold captured:", data);
      storeAPI.dispatch(
        reservationsApi.util.invalidateTags([
          { type: "Reservations", id: data.id || data._id },
          "Reservations",
        ])
      );
      notifyReservationChanged("holdCaptured", data);
    });

    // Hold cancelled
    registerHandler("reservation:holdCancelled", (data) => {
      console.log("📨 [socketMiddleware] Hold cancelled:", data);
      storeAPI.dispatch(
        reservationsApi.util.invalidateTags([
          { type: "Reservations", id: data.id || data._id },
          "Reservations",
        ])
      );
      notifyReservationChanged("holdCancelled", data);
    });

    // Bulk update (multiple reservations changed)
    registerHandler("reservations:bulkUpdate", (data) => {
      console.log("📨 [socketMiddleware] Bulk update:", data);
      storeAPI.dispatch(reservationsApi.util.invalidateTags(["Reservations"]));
      notifyReservationChanged("bulkUpdate", data);
    });

    // Notification created
    registerHandler("notification:created", (data) => {
      console.log("📨 [socketMiddleware] Notification created:", data);

      // New inbound email — refresh inbox list and unread count instantly
      if (data?.type === "inbound-email") {
        storeAPI.dispatch(
          inboundEmailsApi.util.invalidateTags(["InboundEmails", "UnreadCount"])
        );
      }

      // Dispatch custom event for components to listen to
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("redux:notifications/created", { detail: data })
        );
      }
    });

    // Connection status events
    registerHandler("connect", () => {
      console.log("✅ [socketMiddleware] Socket connected");
      // Refetch all reservations on reconnect
      storeAPI.dispatch(
        reservationsApi.util.invalidateTags(["Reservations"])
      );
    });

    registerHandler("disconnect", () => {
      console.log("⚠️ [socketMiddleware] Socket disconnected");
    });

    eventListenersRegistered = true;
    console.log("✅ [socketMiddleware] Event listeners registered");
  };

  /**
   * Clean up socket connection and listeners
   */
  const cleanup = () => {
    if (socketInitialized) {
      console.log("🧹 [socketMiddleware] Cleaning up socket connection...");

      // Remove all registered event listeners
      eventHandlers.forEach((handlers, event) => {
        handlers.forEach(handler => {
          socketService.off(event, handler);
        });
      });
      eventHandlers.clear();

      socketService.disconnect();
      socketInitialized = false;
      eventListenersRegistered = false;
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("beforeunload", cleanup);
    }
  };

  // Initialize socket on middleware creation
  initializeSocket();

  // Cleanup on page unload
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", cleanup);
  }

  // Return middleware function
  return (next) => (action) => {
    // You can intercept specific actions here if needed
    // For example, to trigger socket initialization on login:
    if (action.type === "auth/login/fulfilled") {
      console.log("🔐 [socketMiddleware] User logged in, ensuring socket connection");
      if (!socketInitialized) {
        initializeSocket();
      }
    }

    // You can also handle logout to disconnect socket
    if (action.type === "auth/logout/fulfilled") {
      console.log("🔐 [socketMiddleware] User logged out, disconnecting socket");
      cleanup();
    }

    return next(action);
  };
};

export default socketMiddleware;
